import { ApiResult } from '@src/api/apiClient/apiResult';
import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { useState } from 'react';

interface BaseUploadResponse {
  url: string;
}

interface BaseUploadErrorResponse {
  file?: string[];
}

interface BaseDeleteErrorResponse {
  message?: string;
}

interface UseFileUploadProps<
  TUploadResponse extends BaseUploadResponse,
  TUploadErrorResponse extends BaseUploadErrorResponse,
  TDeleteErrorResponse,
> {
  onSuccess: (url: string, originalFileName?: string) => void;
  onError: (error: string) => void;
  apiClientUploadFile: (file: File) => Promise<ApiResult<TUploadResponse, TUploadErrorResponse>>;
  apiClientDeleteFile: (fileUrl: string) => Promise<ApiResult<unknown, TDeleteErrorResponse>>;
}

export const useFileUpload = <
  TUploadResponse extends BaseUploadResponse,
  TUploadErrorResponse extends BaseUploadErrorResponse,
  TDeleteErrorResponse extends BaseDeleteErrorResponse,
>({
  onSuccess,
  onError,
  apiClientUploadFile,
  // apiClientDeleteFile,
}: UseFileUploadProps<TUploadResponse, TUploadErrorResponse, TDeleteErrorResponse>) => {
  const [isUploading, setIsUploading] = useState(false);
  const [previewBase64, setPreviewBase64] = useState<string | null>(null);

  const handleUpload = async (file: File | null) => {
    if (!file) {
      return null;
    }

    const reader = new FileReader();
    const base64Promise = new Promise<string>((resolve) => {
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(file);
    });
    const base64Data = await base64Promise;
    setPreviewBase64(base64Data);

    setIsUploading(true);
    const response = await apiClientUploadFile(file);

    return new Promise<string | null>((resolve) => {
      handleApiResponse(
        response,
        (data) => {
          onSuccess(data.url, file.name);
          setIsUploading(false);
          resolve(data.url);
        },
        (error) => {
          const errorMessage = error.file?.[0] || 'アップロードに失敗しました';
          onError(errorMessage);
          setIsUploading(false);
          resolve(null);
        },
      );
    });
  };

  const handleDelete = () => {
    setPreviewBase64('');
    onSuccess('', '');
  };

  return { handleUpload, handleDelete, isUploading, previewBase64 };
};
